import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import { getActiveToken } from '../utils/authStorage';
import {
  FaLink,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaSync,
  FaWifi,
  FaUnlink,
  FaCheckCircle,
  FaExclamationCircle,
  FaTimesCircle,
  FaSearch,
  FaChevronDown,
  FaDesktop,
  FaCreditCard,
  FaCircle
} from 'react-icons/fa';
import caisseImg from '../assets/images/caisse.png';
import tpeImg from '../assets/images/tpe.png'; 

const API_BASE = process.env.REACT_APP_DEVICE_SERVICE_URL || '';

/* ----------------------------- Toast / ToastContainer (inchangé) ----------------------------- */
const Toast = ({ notification, onRemove }) => {
  const { id, type, message } = notification;
  
  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove(id);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [id, onRemove]);

  const getToastStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-gradient-to-r from-green-500 to-green-600',
          icon: <FaCheckCircle className="text-white text-base sm:text-lg" />,
          border: 'border-green-300'
        };
      case 'error':
        return {
          bg: 'bg-gradient-to-r from-red-500 to-red-600',
          icon: <FaTimesCircle className="text-white text-base sm:text-lg" />,
          border: 'border-red-300'
        };
      case 'warning':
        return {
          bg: 'bg-gradient-to-r from-orange-500 to-orange-600',
          icon: <FaExclamationCircle className="text-white text-base sm:text-lg" />,
          border: 'border-orange-300'
        };
      case 'loading':
        return {
          bg: 'bg-gradient-to-r from-blue-500 to-blue-600',
          icon: <FaSpinner className="text-white text-base sm:text-lg animate-spin" />,
          border: 'border-blue-300'
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-blue-500 to-blue-600',
          icon: <FaCheckCircle className="text-white text-base sm:text-lg" />,
          border: 'border-blue-300'
        };
    }
  };

  const styles = getToastStyles();

  return (
    <div className={`${styles.bg} ${styles.border} border backdrop-blur-lg rounded-xl shadow-2xl p-3 sm:p-4 mb-2 sm:mb-3 transform transition-all duration-300 ease-in-out animate-slide-in-right w-full max-w-xs sm:max-w-sm`}>
      <div className="flex items-center space-x-2 sm:space-x-3">
        <div className="flex-shrink-0">
          {styles.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium text-xs sm:text-sm leading-relaxed truncate">{message}</p>
        </div>
        <button
          onClick={() => onRemove(id)}
          className="flex-shrink-0 text-white/80 hover:text-white p-1 hover:bg-white/10 rounded-lg transition-all"
        >
          <FaTimes className="text-xs" />
        </button>
      </div>
      
      <div className="mt-2 w-full bg-white/20 rounded-full h-1 overflow-hidden">
        <div className="bg-white h-full rounded-full animate-progress-bar"></div>
      </div>
    </div>
  );
};

const ToastContainer = ({ notifications, removeNotification }) => {
  if (notifications.length === 0) return null;

  return (
    <div className="fixed bottom-2 right-2 sm:bottom-4 sm:right-4 z-[60] space-y-1 sm:space-y-2 w-80 sm:w-auto">
      {notifications.map((notification) => (
        <Toast
          key={notification.id}
          notification={notification}
          onRemove={removeNotification}
        />
      ))}
    </div>
  );
};

const MENU_MAX_HEIGHT = 260;

const computeMenuStyle = (rect) => {
  const spaceBelow = window.innerHeight - rect.bottom;
  const openUpward = spaceBelow < MENU_MAX_HEIGHT && rect.top > spaceBelow;

  return {
    position: 'fixed',
    left: rect.left,
    width: rect.width,
    zIndex: 9999,
    ...(openUpward
      ? { bottom: window.innerHeight - rect.top + 4 }
      : { top: rect.bottom + 4 }),
  };
};

const SearchableSelect = ({
  value,
  onChange,
  options,
  placeholder = 'Sélectionner...',
  disabled = false,
  triggerClassName = '',
  searchPlaceholder = 'Rechercher...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);
  const rafRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const nextStyle = computeMenuStyle(triggerRef.current.getBoundingClientRect());
    setMenuStyle((prev) => {
      if (
        prev &&
        prev.top === nextStyle.top &&
        prev.bottom === nextStyle.bottom &&
        prev.left === nextStyle.left &&
        prev.width === nextStyle.width
      ) {
        return prev;
      }
      return nextStyle;
    });
  }, []);

  const openMenu = useCallback(() => {
    if (!triggerRef.current) return;
    const nextStyle = computeMenuStyle(triggerRef.current.getBoundingClientRect());
    setMenuStyle(nextStyle);
    setIsOpen(true);
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setSearch('');
    setMenuStyle(null);
  }, []);

  const toggleMenu = useCallback(() => {
    if (disabled) return;
    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [closeMenu, disabled, isOpen, openMenu]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const schedulePositionUpdate = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateMenuPosition);
    };

    schedulePositionUpdate();
    window.addEventListener('resize', schedulePositionUpdate);
    window.addEventListener('scroll', schedulePositionUpdate, true);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', schedulePositionUpdate);
      window.removeEventListener('scroll', schedulePositionUpdate, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const focusTimer = requestAnimationFrame(() => {
      searchInputRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(focusTimer);
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inTrigger = containerRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inTrigger && !inMenu) closeMenu();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeMenu]);

  const needle = search.trim().toLowerCase();
  const filteredOptions = useMemo(() => {
    if (!needle) return options;
    return options.filter((opt) =>
      String(opt.label || '').toLowerCase().includes(needle) ||
      String(opt.value || '').toLowerCase().includes(needle)
    );
  }, [needle, options]);

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value),
    [options, value]
  );

  const handleSelect = (optValue) => {
    onChange(optValue);
    closeMenu();
  };

  const dropdownMenu = isOpen && !disabled && menuStyle
    ? createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
        >
          <div className="border-b border-gray-100 p-2">
            <div className="relative">
              <FaSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full rounded-lg border border-gray-200 py-2 pl-8 pr-3 text-xs focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>
          <ul className="max-h-52 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                onClick={() => handleSelect('')}
                className={`w-full px-3 py-2 text-left text-xs hover:bg-gray-50 ${!value ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-500'}`}
              >
                {placeholder}
              </button>
            </li>
            {filteredOptions.length === 0 ? (
              <li className="px-3 py-2 text-center text-xs text-gray-400">Aucun résultat</li>
            ) : (
              filteredOptions.map((opt) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => handleSelect(opt.value)}
                    className={`w-full px-3 py-2 text-left text-xs hover:bg-gray-50 ${
                      value === opt.value ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-800'
                    }`}
                  >
                    {opt.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={containerRef} className="relative z-20 w-full">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={toggleMenu}
        className={`flex w-full items-center justify-between gap-2 text-left ${triggerClassName} ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        <span className="truncate">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <FaChevronDown className={`shrink-0 text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {dropdownMenu}
    </div>
  );
};

/* ----------------------------- Composant Pair (logique adaptée) ----------------------------- */
export default function Pair() {
  const [caisses, setCaisses] = useState([]);
  const [tpes, setTpes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCaisse, setSelectedCaisse] = useState('');
  const [selectedTpe, setSelectedTpe] = useState('');
  const [isPairing, setIsPairing] = useState(false);
  const [currentPairing, setCurrentPairing] = useState(null);
  const [isUnpairing, setIsUnpairing] = useState(false);
  const [pairingInfo, setPairingInfo] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const addNotification = (type, message) => {
    const id = Date.now() + Math.random();
    const newNotification = { id, type, message };
    setNotifications(prev => [...prev, newNotification]);
  };
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const getToken = () => getActiveToken() || null;
  const getAuthHeaders = () => {
    const token = getToken();
    if (!token) return null;
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  /* ----------------------------- fetchPairingInfo (inchangé mais robuste) ----------------------------- */
  const fetchPairingInfo = async (clientId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) {
        setError('Token manquant');
        addNotification('error', 'Token manquant');
        return;
      }

      const response = await fetch(`${API_BASE}/devices/${clientId}/pairing`, {
        method: 'GET',
        mode: 'cors',
        headers
      });

      if (!response.ok) {
        const t = await response.text();
        throw new Error(t || `HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { message: text }; }
      
      if (data.success) {
        setPairingInfo(data);
        if (data.paired) {
          setCurrentPairing({
            caisseId: data.device ? data.device.clientId : data.caisseId || clientId,
            tpeId: (data.pairedDevice && data.pairedDevice.clientId) || (data.tpeId) || '',
            sessionId: data.session && data.session.sessionId
          });
          setSelectedTpe((data.pairedDevice && data.pairedDevice.clientId) || '');
        } else {
          setCurrentPairing(null);
          setSelectedTpe('');
        }
      } else {
        // si backend renvoie success:false
        console.error('Erreur dans la réponse pairing:', data.error || data.message);
        setError(data.error || data.message || 'Erreur pairing');
        setPairingInfo(null);
        setCurrentPairing(null);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des infos de pairing:', err);
      setError(err.message);
      addNotification('error', `Erreur pairing: ${err.message}`);
      setPairingInfo(null);
      setCurrentPairing(null);
    }
  };

  /* ----------------------------- checkCanPair (GET /devices/:clientId/canPair?tpeId=...) ----------------------------- */
  const checkCanPair = async (caisseId, tpeId) => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return { canPair: false, reason: 'Token manquant' };

      const response = await fetch(`${API_BASE}/devices/${caisseId}/canPair?tpeId=${encodeURIComponent(tpeId)}`, {
        method: 'GET',
        mode: 'cors',
        headers
      });

      if (response.status === 403) {
        return { canPair: false, reason: 'Accès refusé (rôle insuffisant)' };
      }

      if (!response.ok) {
        const t = await response.text();
        throw new Error(t || `HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { message: text }; }
      // backend renvoie { success: true, canPair: true, ... } — on forward
      return data;
    } catch (error) {
      console.error('Erreur lors de la vérification de l\'appairage:', error);
      return { canPair: false, reason: 'Erreur réseau ou serveur' };
    }
  };

  /* ----------------------------- fetchDevices : adapté au backend ----------------------------- */
  const fetchDevices = async () => {
    setLoading(true);
    setError(null);

    try {
      const headers = getAuthHeaders(); // optional for /devices but may be helpful for /pairing later
      // On récupère caisses (avec pairing) et tpes (avec pairing) puis on appelle /tpes pour l'état websocket
      const caisseUrl = `${API_BASE}/devices?clientType=CAISSE&includePairing=true&limit=10000`;
      const tpeDevicesUrl = `${API_BASE}/devices?clientType=TPE&includePairing=true&limit=10000`;
      const tpeConnUrl = `${API_BASE}/tpes`;

      const [resCaisse, resTpeDevices, resTpeConn] = await Promise.all([
        fetch(caisseUrl, { mode: 'cors', headers: headers || {} }),
        fetch(tpeDevicesUrl, { mode: 'cors', headers: headers || {} }),
        fetch(tpeConnUrl, { mode: 'cors', headers: headers || {} })
      ]);

      if (!resCaisse.ok) {
        const text = await resCaisse.text();
        throw new Error(`Erreur fetch caisses: ${text || resCaisse.status}`);
      }
      if (!resTpeDevices.ok) {
        const text = await resTpeDevices.text();
        throw new Error(`Erreur fetch tpe devices: ${text || resTpeDevices.status}`);
      }
      if (!resTpeConn.ok) {
        // on ne bloque pas tout si /tpes échoue, on continue sans connected info
        console.warn('Warning: /tpes returned non-ok, continuing without connection states');
      }

      const dataCaissesText = await resCaisse.text();
      const dataTpesText = await resTpeDevices.text();
      const dataTpeConnText = await (resTpeConn.ok ? resTpeConn.text() : Promise.resolve(null));

      let dataCaisses = { devices: [] }, dataTpes = { devices: [] }, tpeConns = [];
      try { const parsed = JSON.parse(dataCaissesText); dataCaisses = parsed; } catch { dataCaisses = { devices: [] }; }
      try { const parsed = JSON.parse(dataTpesText); dataTpes = parsed; } catch { dataTpes = { devices: [] }; }
      try { if (dataTpeConnText) { tpeConns = JSON.parse(dataTpeConnText); } } catch { tpeConns = []; }

      const caissesArray = Array.isArray(dataCaisses.devices) ? dataCaisses.devices : [];
      const activeCaisses = caissesArray
        .filter(c => c.status === 'active')
        .sort((a, b) =>
          String(a.clientId ?? '').localeCompare(String(b.clientId ?? ''), 'fr', { sensitivity: 'base' })
        );

      // tpes devices (from /devices?clientType=TPE...) includes pairing, but we also want the connected state from /tpes
      const tpesArray = Array.isArray(dataTpes.devices) ? dataTpes.devices : [];
      const activeTpes = tpesArray
        .filter(t => t.status === 'active')
        .map(t => {
          const conn = Array.isArray(tpeConns) ? tpeConns.find(x => x.clientId === t.clientId) : undefined;
          return {
            ...t,
            connected: conn ? !!conn.connected : false
          };
        });

      setCaisses(activeCaisses);
      setTpes(activeTpes);
    } catch (err) {
      console.error('Erreur lors du chargement des devices:', err);
      setError(err.message || String(err));
      addNotification('error', `Erreur lors du chargement: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  /* ----------------------------- effets ----------------------------- */
  useEffect(() => {
    if (selectedCaisse && caisses.length > 0) {
      fetchPairingInfo(selectedCaisse);
    } else {
      setCurrentPairing(null);
      setPairingInfo(null);
      setSelectedTpe('');
    }
  }, [selectedCaisse, caisses]);

  useEffect(() => {
    fetchDevices();
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    addNotification('loading', 'Rafraîchissement en cours...');
    
    await fetchDevices();
    
    if (selectedCaisse) {
      await fetchPairingInfo(selectedCaisse);
    }
    
    setIsRefreshing(false);
    addNotification('success', 'Données rafraîchies avec succès');
  };

  const handleUnpair = async () => {
    if (!currentPairing || !selectedCaisse) return;

    setIsUnpairing(true);

    try {
      const headers = getAuthHeaders();
      if (!headers) {
        addNotification('error', 'Token manquant');
        setIsUnpairing(false);
        return;
      }

      const response = await fetch(`${API_BASE}/devices/${selectedCaisse}/pair`, {
        method: 'DELETE',
        mode: 'cors',
        headers
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { message: text }; }
      
      if (!data.success) {
        throw new Error(data.message || data.error || 'Erreur lors du dépairage');
      }

      addNotification('success', 'Dépairage réussi avec succès');
      setCurrentPairing(null);
      setSelectedTpe('');
      setPairingInfo(null);

      await fetchDevices();

    } catch (err) {
      console.error('Erreur lors du dépairage:', err);
      addNotification('error', `Erreur lors du dépairage: ${err.message}`);
    } finally {
      setIsUnpairing(false);
    }
  };

  const handlePair = async () => {
    if (!selectedCaisse || !selectedTpe) {
      addNotification('warning', 'Veuillez sélectionner une caisse et un TPE');
      return;
    }

    const canPairResult = await checkCanPair(selectedCaisse, selectedTpe);
    if (!canPairResult.canPair) {
      addNotification('error', `Impossible d'appairer: ${canPairResult.reason || (canPairResult.message) || 'Raison inconnue'}`);
      return;
    }

    setIsPairing(true);

    try {
      const headers = getAuthHeaders();
      if (!headers) {
        addNotification('error', 'Token manquant');
        setIsPairing(false);
        return;
      }

      const response = await fetch(`${API_BASE}/devices/${selectedCaisse}/pair`, {
        method: 'POST',
        mode: 'cors',
        headers,
        body: JSON.stringify({
          targetDeviceId: selectedTpe,
          metadata: {
            createdBy: 'admin-interface',
            timestamp: new Date().toISOString()
          }
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      const text = await response.text();
      let data;
      try { data = JSON.parse(text); } catch { data = { message: text }; }
      
      if (!data.success) {
        throw new Error(data.message || data.error || "Erreur lors de l'appairage");
      }

      addNotification('success', 'Appairage réalisé avec succès');

      await fetchPairingInfo(selectedCaisse);
      await fetchDevices();

    } catch (err) {
      console.error("Erreur lors de l'appairage:", err);
      addNotification('error', `Erreur lors de l'appairage: ${err.message}`);
    } finally {
      setIsPairing(false);
    }
  };

  const resetSelection = () => {
    setSelectedCaisse('');
    setSelectedTpe('');
    setCurrentPairing(null);
    setPairingInfo(null);
    setError(null);
  };

  const isSelectedCaissePaired = () => {
    return currentPairing && currentPairing.caisseId === selectedCaisse;
  };

  const caisseOptions = useMemo(
    () => caisses.map((c) => ({
      value: c.clientId,
      label: `${c.clientId} - ${c.pairing && c.pairing.paired ? `(${c.pairing.pairedDevice.clientId})` : '(Libre)'}`,
    })),
    [caisses]
  );

  const tpeOptions = useMemo(
    () => tpes.map((t) => ({
      value: t.clientId,
      label: `${t.clientId}  - ${t.pairing && t.pairing.paired ? `(${t.pairing.pairedDevice.clientId})` : '(Libre)'}`,
    })),
    [tpes]
  );

  const pairingStats = useMemo(() => ({
    caisses: caisses.length,
    tpes: tpes.length,
    paired: caisses.filter((c) => c.pairing?.paired).length,
    freeCaisses: caisses.filter((c) => !c.pairing?.paired).length,
    freeTpes: tpes.filter((t) => !t.pairing?.paired).length,
  }), [caisses, tpes]);

  const isPaired = isSelectedCaissePaired();
  const canPair = selectedCaisse && selectedTpe && !currentPairing;

  /* ----------------------------- rendu ----------------------------- */
  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Sidebar />

      <div className="flex flex-1 flex-col min-w-0 overflow-hidden">
        <Navbar />

        <div className="shrink-0 border-b bg-white shadow-sm">
          <div className="p-3 sm:p-4 lg:p-6">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-r from-blue-600 to-blue-700">
                  <FaLink className="text-sm text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-900 sm:text-xl">Appairage</h1>
                  <p className="text-xs text-gray-500 sm:text-sm">Connectez une caisse à un terminal de paiement</p>
                </div>
              </div>

              <button
                onClick={handleRefresh}
                disabled={isRefreshing || loading}
                className="flex items-center self-start rounded-lg border px-3 py-2 text-xs font-medium hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 sm:self-auto"
              >
                <FaSync className={`mr-1.5 text-xs ${isRefreshing ? 'animate-spin' : ''}`} />
                Actualiser
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-2 md:grid-cols-4">
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-blue-200/70 bg-blue-50/50 px-2.5 py-2 shadow-sm ring-1 ring-blue-900/5">
                <FaDesktop className="h-3.5 w-3.5 shrink-0 text-blue-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-blue-700/90">Caisses</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-blue-900">{pairingStats.caisses}</p>
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-violet-200/70 bg-violet-50/50 px-2.5 py-2 shadow-sm ring-1 ring-violet-900/5">
                <FaCreditCard className="h-3.5 w-3.5 shrink-0 text-violet-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-violet-800/90">TPE</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-violet-950">{pairingStats.tpes}</p>
                </div>
              </div>
              <div className="flex min-w-0 items-center gap-2 rounded-xl border border-emerald-200/70 bg-emerald-50/50 px-2.5 py-2 shadow-sm ring-1 ring-emerald-900/5">
                <FaWifi className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-emerald-700/90">Appairées</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-emerald-900">{pairingStats.paired}</p>
                </div>
              </div>
              <div className="col-span-2 flex min-w-0 items-center gap-2 rounded-xl border border-amber-200/70 bg-amber-50/40 px-2.5 py-2 shadow-sm ring-1 ring-amber-900/5 md:col-span-1">
                <FaCircle className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                <div className="min-w-0">
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-amber-800/90">Libres</p>
                  <p className="text-sm font-bold tabular-nums tracking-tight text-amber-950">
                    {pairingStats.freeCaisses} / {pairingStats.freeTpes}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <main className="min-h-0 flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
          {loading && (
            <div className="flex h-64 flex-col items-center justify-center">
              <FaSpinner className="mb-3 text-3xl animate-spin text-blue-600" />
              <p className="text-sm font-medium text-gray-600">Chargement des équipements…</p>
            </div>
          )}

          {error && !loading && (
            <div className="mx-auto max-w-lg rounded-xl border border-red-200 bg-red-50 p-4 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
                  <FaTimes className="text-red-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="mb-1 text-sm font-semibold text-red-800">Erreur</h3>
                  <p className="mb-3 text-sm text-red-700">{error}</p>
                  <button
                    onClick={resetSelection}
                    className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
                  >
                    Réessayer
                  </button>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && (
            <div className="mx-auto w-full max-w-6xl xl:max-w-7xl">
              <div className="overflow-visible rounded-xl border bg-white shadow-sm">
                {currentPairing && (
                  <div className={`border-b px-4 py-3 sm:px-6 ${
                    isPaired ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'
                  }`}>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-2">
                        <FaWifi className={isPaired ? 'text-green-600' : 'text-orange-600'} />
                        <span className={`text-sm font-semibold ${isPaired ? 'text-green-800' : 'text-orange-800'}`}>
                          Connexion active
                        </span>
                      </div>
                      <p className={`font-mono text-xs break-all ${isPaired ? 'text-green-700' : 'text-orange-700'}`}>
                        {currentPairing.caisseId} ⟷ {currentPairing.tpeId}
                      </p>
                      <button
                        onClick={resetSelection}
                        className={`self-start text-xs underline sm:self-auto ${isPaired ? 'text-green-700 hover:text-green-900' : 'text-orange-700 hover:text-orange-900'}`}
                      >
                        Réinitialiser
                      </button>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 items-stretch gap-6 overflow-visible p-4 sm:p-6 lg:grid-cols-[1fr_minmax(180px,220px)_1fr] lg:gap-6 xl:grid-cols-[1fr_minmax(200px,260px)_1fr] xl:gap-8 xl:p-7 2xl:gap-10 2xl:p-8">
                  {/* Caisse */}
                  <div className={`flex h-full flex-col overflow-visible rounded-xl border-2 p-4 transition-colors sm:p-5 xl:p-6 ${
                    selectedCaisse ? 'border-blue-400 bg-blue-50/40' : 'border-gray-200 bg-gray-50/50'
                  }`}>
                    <div className="mb-4 flex min-h-[2rem] shrink-0 items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FaDesktop className="text-blue-600" />
                        <h3 className="text-sm font-bold text-gray-900 sm:text-base">Caisse</h3>
                      </div>
                      {selectedCaisse ? (
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-800">
                          <FaCheck className="mr-1 text-[10px]" />
                          Sélectionnée
                        </span>
                      ) : (
                        <span className="inline-block w-[5.5rem]" aria-hidden="true" />
                      )}
                    </div>

                    <div className="mb-4 flex min-h-[9rem] flex-1 items-center justify-center rounded-lg bg-white py-5 ring-1 ring-gray-100 sm:min-h-[10rem] xl:min-h-[11rem] xl:py-6">
                      <img
                        src={caisseImg}
                        alt="Caisse"
                        loading="lazy"
                        className="h-auto w-24 object-contain sm:w-32 lg:w-36 xl:w-40 2xl:w-44"
                      />
                    </div>

                    <div className="mt-auto shrink-0">
                      <SearchableSelect
                        value={selectedCaisse}
                        onChange={setSelectedCaisse}
                        options={caisseOptions}
                        placeholder="Sélectionner une caisse"
                        searchPlaceholder="Rechercher une caisse..."
                        triggerClassName="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-left text-xs font-medium shadow-sm transition-colors hover:border-blue-300 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200 sm:text-sm"
                      />
                    </div>
                  </div>

                  {/* Centre — action */}
                  <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-gray-100 bg-gray-50/80 px-4 py-6 lg:px-5 lg:py-8 xl:gap-5 xl:px-6 xl:py-9 2xl:px-7 2xl:py-10">
                    <div className="hidden h-px w-full bg-gradient-to-r from-blue-200 via-gray-300 to-emerald-200 lg:block" />

                    <div className="relative">
                      <div className={`absolute inset-0 rounded-full blur-lg opacity-40 ${
                        currentPairing ? 'bg-green-400' : 'bg-red-400'
                      }`} />
                      {currentPairing ? (
                        <button
                          onClick={handleUnpair}
                          disabled={isUnpairing}
                          className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg transition-transform hover:scale-105 disabled:opacity-50 sm:h-[4.5rem] sm:w-[4.5rem] lg:h-[4.75rem] lg:w-[4.75rem] xl:h-20 xl:w-20 2xl:h-[5.25rem] 2xl:w-[5.25rem]"
                        >
                          {isUnpairing ? (
                            <FaSpinner className="animate-spin text-2xl xl:text-[1.65rem] 2xl:text-3xl" />
                          ) : (
                            <FaLink className="text-2xl xl:text-[1.65rem] 2xl:text-3xl" />
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={handlePair}
                          disabled={!canPair || isPairing}
                          className={`relative flex h-16 w-16 items-center justify-center rounded-full text-white shadow-lg transition-all hover:scale-105 disabled:scale-100 sm:h-[4.5rem] sm:w-[4.5rem] lg:h-[4.75rem] lg:w-[4.75rem] xl:h-20 xl:w-20 2xl:h-[5.25rem] 2xl:w-[5.25rem] ${
                            isPairing
                              ? 'animate-pulse bg-gradient-to-r from-yellow-400 to-orange-500'
                              : 'bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 disabled:from-red-400 disabled:to-rose-500 disabled:opacity-60'
                          }`}
                        >
                          {isPairing ? (
                            <FaSpinner className="animate-spin text-2xl xl:text-[1.65rem] 2xl:text-3xl" />
                          ) : (
                            <FaUnlink className="text-2xl xl:text-[1.65rem] 2xl:text-3xl" />
                          )}
                        </button>
                      )}
                    </div>

                    <p className="max-w-[180px] text-center text-xs font-medium text-gray-600 sm:max-w-[200px] sm:text-sm xl:max-w-[220px]">
                      {currentPairing
                        ? 'Déconnecter les équipements'
                        : canPair
                          ? 'Lancer l\'appairage'
                          : 'Sélectionnez une caisse et un TPE'}
                    </p>

                    <div className="hidden h-px w-full bg-gradient-to-r from-blue-200 via-gray-300 to-emerald-200 lg:block" />
                  </div>

                  {/* TPE */}
                  <div className={`flex h-full flex-col overflow-visible rounded-xl border-2 p-4 transition-colors sm:p-5 xl:p-6 ${
                    selectedTpe ? 'border-emerald-400 bg-emerald-50/40' : 'border-gray-200 bg-gray-50/50'
                  }`}>
                    <div className="mb-4 flex min-h-[2rem] shrink-0 items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FaCreditCard className="text-emerald-600" />
                        <h3 className="text-sm font-bold text-gray-900 sm:text-base">Terminal TPE</h3>
                      </div>
                      {selectedTpe ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
                          <FaCheck className="mr-1 text-[10px]" />
                          Sélectionné
                        </span>
                      ) : (
                        <span className="inline-block w-[5.5rem]" aria-hidden="true" />
                      )}
                    </div>

                    <div className="mb-4 flex min-h-[9rem] flex-1 items-center justify-center rounded-lg bg-white py-5 ring-1 ring-gray-100 sm:min-h-[10rem] xl:min-h-[11rem] xl:py-6">
                      <img
                        src={tpeImg}
                        alt="TPE"
                        loading="lazy"
                        className="h-auto w-24 object-contain sm:w-32 lg:w-36 xl:w-40 2xl:w-44"
                      />
                    </div>

                    <div className="mt-auto shrink-0">
                      <SearchableSelect
                        value={selectedTpe}
                        onChange={setSelectedTpe}
                        options={tpeOptions}
                        placeholder="Sélectionner un TPE"
                        searchPlaceholder="Rechercher un TPE..."
                        disabled={!!currentPairing}
                        triggerClassName={`w-full rounded-lg border px-3 py-2.5 text-left text-xs font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 sm:text-sm ${
                          currentPairing
                            ? 'cursor-not-allowed border-orange-300 bg-orange-50 text-orange-700'
                            : 'border-gray-300 bg-white hover:border-emerald-300 focus:border-emerald-500 focus:ring-emerald-200'
                        }`}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      <ToastContainer 
        notifications={notifications} 
        removeNotification={removeNotification} 
      />

      <style >{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes progress-bar {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
        
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
        
        .animate-progress-bar {
          animation: progress-bar 2s linear;
        }

        @media (max-width: 640px) {
          .toast-mobile {
            bottom: 60px;
            right: 10px;
            left: 10px;
            width: auto;
          }
        }

        @media (max-width: 480px) {
          .text-responsive {
            font-size: 0.875rem;
            line-height: 1.25rem;
          }
        }

        @media (min-width: 768px) and (max-width: 1024px) {
          .tablet-optimization {
            padding: 1.5rem;
          }
        }

        @media (min-width: 1920px) {
          .large-screen-optimization {
            max-width: 90%;
          }
        }
      `}</style>
    </div>
  );
}
